import { useTypingEffect } from "@/hooks/useTypingEffect";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Github, Linkedin, Mail } from "lucide-react";

import { socialLinks } from "@/data/contact";

export function About() {
  const { displayed: greeting, isDone: greetingDone } = useTypingEffect(
    "Hello, I'm Khang.",
    { speed: 60, startDelay: 500 },
  );

  const { displayed: tagline, isDone: taglineDone } = useTypingEffect(
    "Fullstack developer passionate about technology and continuous learning.",
    { speed: 30, enabled: greetingDone },
  );

  const contentRef = useScrollReveal<HTMLDivElement>({ y: 20, delay: 0.2 });

  return (
    <section id="about" className="min-h-screen flex items-center px-6 pt-20">
      <div className="max-w-3xl mx-auto w-full">
        {/* Terminal window frame */}
        <div className="border border-border rounded-lg overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-2 bg-secondary border-b border-border">
            <div className="w-3 h-3 rounded-full bg-terminal-red" />
            <div className="w-3 h-3 rounded-full bg-terminal-yellow" />
            <div className="w-3 h-3 rounded-full bg-terminal-green" />
            <span className="ml-3 text-xs text-muted-foreground">
              khang@portfolio:~
            </span>
          </div>

          {/* Terminal content */}
          <div className="p-6 space-y-4 bg-background">
            <div>
              <span className="text-terminal-green">$</span>
              <span className="text-terminal-cyan ml-2">cat</span>
              <span className="text-foreground ml-2">about.md</span>
            </div>

            <h1 className="text-3xl font-bold text-terminal-bright">
              {greeting}
              {!greetingDone && (
                <span className="cursor-blink text-terminal-green">_</span>
              )}
            </h1>

            {greetingDone && (
              <p className="text-lg text-foreground">
                {tagline}
                {!taglineDone && (
                  <span className="cursor-blink text-terminal-green">_</span>
                )}
              </p>
            )}

            {taglineDone && (
              <div
                ref={contentRef}
                className="space-y-4 pt-4 border-t border-border"
              >
                <p
                  data-reveal
                  className="text-muted-foreground leading-relaxed"
                >
                  I'm a problem solver at heart — I love understanding the{" "}
                  <span className="text-terminal-cyan">why</span> behind
                  everything. My strength is in backend development, but I enjoy
                  working across the full stack, from crafting APIs to building
                  interfaces and setting up infrastructure.
                </p>

                <p
                  data-reveal
                  className="text-muted-foreground leading-relaxed"
                >
                  I believe in{" "}
                  <span className="text-terminal-yellow">
                    continuous learning
                  </span>{" "}
                  and building things that matter. When I'm not coding, I'm
                  exploring new technologies, reading about system design, or
                  finding the next thing to be curious about.
                </p>

                <div data-reveal className="flex gap-4 pt-4">
                  <a
                    href={socialLinks.github.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-terminal-green transition-colors"
                  >
                    <Github size={16} />
                    <span>github</span>
                  </a>
                  <a
                    href={socialLinks.linkedin.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-terminal-cyan transition-colors"
                  >
                    <Linkedin size={16} />
                    <span>linkedin</span>
                  </a>
                  <a
                    href={socialLinks.email.url}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-terminal-yellow transition-colors"
                  >
                    <Mail size={16} />
                    <span>email</span>
                  </a>
                </div>

                <div data-reveal className="pt-4">
                  <span className="text-terminal-green">$</span>
                  <span className="cursor-blink text-terminal-green ml-2">
                    _
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
